import config from "../../config.json";
import { isWhiskymofo } from "./is-page";

interface BreadcrumbItem {
  name: string;
  url: string;
  position: number;
}

interface BreadcrumbListItem {
  "@type": string;
  position: number;
  name: string;
  item?: string;
}

interface BreadcrumbSchema {
  "@context": string;
  "@type": string;
  itemListElement: BreadcrumbListItem[];
}

const getBaseUrlForBreadcrumbs = (): string => {
  const market = process.env.GATSBY_VM_MARKET as keyof typeof config;

  if (isWhiskymofo) {
    return process.env.GATSBY_APP_URL || "https://www.whiskymofo.com";
  }

  return config[market].HOST || process.env.GATSBY_APP_URL;
};

const formatBreadcrumbName = (
  segment: string,
  currentPath: string,
  _allSegments: string[]
): string => {
  const categoryMap: Record<string, string> = {
    wines: "Wines",
    "red-wine": "Red Wine",
    "red-wines": "Red Wine",
    "white-wine": "White Wine",
    rose: "Rosé",
    sparkling: "Sparkling",
    "mixed-case": "Mixed Cases",
    mixed: "Mixed Cases",
    spirits: "Spirits",
    scotch: "Scotch",
    bourbon: "Bourbon",
    japanese: "Japanese Whisky",
    gin: "Gin",
    rum: "Rum",
    whisky: "Whisky",
    vodka: "Vodka",
    brandy: "Brandy",
    tequila: "Tequila",
    cognac: "Cognac",
    mezcal: "Mezcal",
    agave: "Agave",
    liqueur: "Liqueur",
    cocktail: "Cocktail",
    events: "Events",
    "wine-clubs": "Wine Clubs",
    loyalty: "Loyalty",
    "guest-checkout": "Guest Checkout",
    "tier-offers": "Tier Offers",
    "black-market-club": "Black Market Club",
    "mofo-club": "Mofo Club",
    articles: "Vinomofo Articles",
    "whiskymofo-articles": "Whisky Articles",
    "vino-articles": "Vinomofo Articles",
  };

  if (categoryMap[segment]) {
    return categoryMap[segment];
  }
  if (currentPath.includes("/wines/") || currentPath.includes("/spirits/")) {
    return segment
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }

  return segment
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const generateBreadcrumbSchema = (
  pathname: string,
  pageTitle?: string
): BreadcrumbSchema | null => {
  if (pathname === "/" || pathname === "") {
    return null;
  }

  const breadcrumbs: BreadcrumbItem[] = [];
  const segments = pathname.split("/").filter(Boolean);

  breadcrumbs.push({
    name: "Home",
    url: "/",
    position: 1,
  });

  let currentPath = "";
  let position = 2;

  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];
    currentPath += `/${segment}`;

    let breadcrumbName = formatBreadcrumbName(segment, currentPath, segments);

    if (i === segments.length - 1 && pageTitle) {
      breadcrumbName = pageTitle;
    }

    breadcrumbs.push({
      name: breadcrumbName,
      url: currentPath,
      position: position++,
    });
  }

  const baseUrl = getBaseUrlForBreadcrumbs();

  const itemListElement = breadcrumbs.map((breadcrumb, index) => {
    const isLastItem = index === breadcrumbs.length - 1;
    const breadcrumbItem: BreadcrumbListItem = {
      "@type": "ListItem",
      position: breadcrumb.position,
      name: breadcrumb.name,
    };
    if (!isLastItem) {
      breadcrumbItem.item = `${baseUrl}${breadcrumb.url}`;
    }

    return breadcrumbItem;
  });

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement,
  };
};
